import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Home, Search, Users, LogOut, User, Shield, Menu, X, Moon, Sun, HelpCircle, GitBranch } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import NotificationCenter from './NotificationCenter';

export default function Navigation() {
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="border-b border-slate-700 sticky top-0 z-50 backdrop-blur-md bg-slate-800/95">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-[72px]">
          <Link to="/" className="flex items-center hover:opacity-90 transition-opacity flex-shrink-0 mr-4 sm:mr-6 lg:mr-10">
            {/* Full logo lockup on all devices - responsive sizing */}
            {/* Mobile: 32px */}
            <img 
              src="/assets/seedsynch-logo-lockup.svg" 
              alt="SeedSynch - Smarter connections, earlier." 
              className="sm:hidden"
              style={{ height: '32px', width: 'auto' }}
            />
            {/* Small tablet: 40px */}
            <img 
              src="/assets/seedsynch-logo-lockup.svg" 
              alt="SeedSynch - Smarter connections, earlier." 
              className="hidden sm:block md:hidden"
              style={{ height: '40px', width: 'auto' }}
            />
            {/* Tablet: 48px */}
            <img 
              src="/assets/seedsynch-logo-lockup.svg" 
              alt="SeedSynch - Smarter connections, earlier." 
              className="hidden md:block lg:hidden"
              style={{ height: '48px', width: 'auto' }}
            />
            {/* Desktop: 56px */}
            <img 
              src="/assets/seedsynch-logo-lockup.svg" 
              alt="SeedSynch - Smarter connections, earlier." 
              className="hidden lg:block"
              style={{ height: '56px', width: 'auto' }}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            <Link
              to="/"
              data-testid="nav-home"
              className={`nav-link flex items-center gap-2 text-sm font-medium transition-colors ${
                isActive('/') 
                  ? 'text-blue-500' 
                  : 'text-slate-300 hover:text-blue-400'
              }`}
            >
              <Home size={18} />
              <span>Home</span>
            </Link>

            {user?.role !== 'admin' && (
              <>
                <Link
                  to="/discovery"
                  data-testid="nav-discovery"
                  className={`nav-link flex items-center gap-2 text-sm font-medium transition-colors ${
                    isActive('/discovery') 
                      ? 'text-blue-500' 
                      : 'text-slate-300 hover:text-blue-400'
                  }`}
                >
                  <Search size={18} />
                  <span>Discover</span>
                </Link>

                <Link
                  to="/matches"
                  data-testid="nav-matches"
                  className={`nav-link flex items-center gap-2 text-sm font-medium transition-colors ${
                    isActive('/matches') 
                      ? 'text-blue-500' 
                      : 'text-slate-300 hover:text-blue-400'
                  }`}
                >
                  <Users size={18} />
                  <span>Matches</span>
                </Link>

                <Link
                  to="/relationships"
                  data-testid="nav-progress"
                  className={`nav-link flex items-center gap-2 text-sm font-medium transition-colors ${
                    isActive('/relationships') 
                      ? 'text-blue-500' 
                      : 'text-slate-300 hover:text-blue-400'
                  }`}
                >
                  <GitBranch size={18} />
                  <span>Progress</span>
                </Link>
              </>
            )}

            {/* Help Link */}
            <Link
              to="/help"
              data-testid="nav-help"
              className={`nav-link flex items-center gap-2 text-sm font-medium transition-colors ${
                isActive('/help') 
                  ? 'text-blue-500' 
                  : 'text-slate-300 hover:text-blue-400'
              }`}
            >
              <HelpCircle size={18} />
              <span>Help</span>
            </Link>

            <div className={`flex items-center gap-3 ml-4 pl-4 border-l ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
              {/* Dark Mode Toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-md transition-colors text-white hover:text-blue-300 hover:bg-slate-700"
                aria-label="Toggle dark mode"
                data-testid="theme-toggle"
              >
                {isDark ? <Sun size={18} /> : <Moon size={18} />}
              </button>

              {/* Notification Center */}
              <NotificationCenter />

              <Link
                to="/account"
                data-testid="nav-account"
                className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                  isActive('/account') 
                    ? 'text-blue-400' 
                    : 'text-white hover:text-blue-300'
                }`}
              >
                {user?.role === 'admin' ? <Shield size={18} /> : <User size={18} />}
                <span className="hidden lg:inline">{user?.name}</span>
              </Link>
              <Button
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                data-testid="logout-button"
                className="text-white hover:text-blue-300 hover:bg-slate-700"
              >
                <LogOut size={18} />
              </Button>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-md transition-colors text-white hover:text-blue-300 hover:bg-slate-700"
              aria-label="Toggle dark mode"
            >
              {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <button
              className="p-2 text-white hover:text-blue-300"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t bg-slate-800 border-slate-700">
          <div className="px-4 py-4 space-y-3">
            <Link
              to="/"
              className={`flex items-center gap-3 text-sm font-medium py-2 ${
                isActive('/') 
                  ? 'text-blue-400' 
                  : 'text-white'
              }`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <Home size={18} />
              <span>Home</span>
            </Link>

            {user?.role !== 'admin' && (
              <>
                <Link
                  to="/discovery"
                  className={`flex items-center gap-3 text-sm font-medium py-2 ${
                    isActive('/discovery') 
                      ? 'text-blue-400' 
                      : 'text-white'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Search size={18} />
                  <span>Discover</span>
                </Link>

                <Link
                  to="/matches"
                  className={`flex items-center gap-3 text-sm font-medium py-2 ${
                    isActive('/matches') 
                      ? 'text-blue-500' 
                      : 'text-white'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Users size={18} />
                  <span>Matches</span>
                </Link>

                <Link
                  to="/relationships"
                  className={`flex items-center gap-3 text-sm font-medium py-2 ${
                    isActive('/relationships') 
                      ? 'text-blue-500' 
                      : 'text-white'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <GitBranch size={18} />
                  <span>Progress</span>
                </Link>
              </>
            )}

            {/* Help Link - Mobile */}
            <Link
              to="/help"
              className={`flex items-center gap-3 text-sm font-medium py-2 ${
                isActive('/help') 
                  ? 'text-blue-500' 
                  : 'text-white'
              }`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <HelpCircle size={18} />
              <span>Help</span>
            </Link>

            <div className={`pt-4 border-t space-y-3 ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
              <Link
                to="/account"
                className={`flex items-center gap-3 text-sm font-medium py-2 ${
                  isActive('/account') 
                    ? 'text-blue-500' 
                    : 'text-white'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {user?.role === 'admin' ? <Shield size={18} /> : <User size={18} />}
                <span>{user?.name}</span>
              </Link>
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleLogout();
                }}
                className="flex items-center gap-3 text-sm font-medium py-2 w-full text-white hover:text-blue-300"
              >
                <LogOut size={18} />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
